// Annual contract utilisation for one scope, as the tile row above the tabs
// shows it (INFS-1454).
//
// Pure by design, in the same spirit as src/kjp.js: no Vue, no API, no DOM, so
// the figures can be asserted from scripts/verify-kjp.mjs rather than
// eyeballed in a component. It derives nothing new — every number here already
// exists on the model buildKjpModel returned, and this file only selects and
// divides. Any new arithmetic belongs in src/kjp.js beside the rest of the
// quota maths, not here.
//
// THE PERIOD RULE (INFS-1454 criterion 3): these figures must respond to the
// selected year and district set, and to nothing else — changing "Contract
// Starts", "Months" or "As At" must leave every tile untouched. That holds
// structurally rather than by convention, because this file reads only the
// ceiling row's `usedAnnual` / `bakiAnnual`:
//
//   usedAnnual  = sum(1, 12)               — all twelve calendar months
//   bakiAnnual  = annualQuota - usedAnnual — annualQuota takes only the
//                                            district's road lengths
//
// Neither expression mentions startMonth, monthCount or asAtMonth. The
// `usedToDate` / `bakiToDate` pair right beside them on the same row DOES
// depend on all three, so reading those instead is the one change that would
// silently break the rule — hence this comment sitting where that mistake
// would be made.

import { scopeUnit } from '@/components/kjp/scopes'

// What the tile can say about a scope. Ordered by how much is known.
//
// 'failed' and 'empty' are distinguishable on purpose, and neither collapses
// into a figure: a scope whose fetch threw has *unknown* utilisation, and a
// scope with no cases has *no* utilisation, and both are different from a
// scope that genuinely used none of its quota. Rendering any of them as
// "0.00 (0.0%)" would read as "we measured, and the answer was nothing" —
// the same class of lie src/kjp.js's `unmeasured` flag exists to prevent.
export const UTILISATION_STATES = ['ok', 'over', 'unmeasured', 'empty', 'failed']

function pct(value, total) {
  return total ? (value / total) * 100 : 0
}

// Build one tile's view-model.
//
// `model` is the built KJP model for this scope, or null when the scope
// fetched cleanly but had nothing to show. `failed` is the scope's entry in
// KjpSummary's `scopeErrors` — kept a separate argument rather than inferred,
// because a failed fetch produces no model at all and would otherwise be
// indistinguishable from an empty one.
export function scopeUtilisation({ scope, model, failed }) {
  const base = { scope, unit: scopeUnit(scope) }

  if (failed) return { ...base, state: 'failed' }
  if (!model || !model.totals || !model.rows || !model.rows.length) {
    return { ...base, state: 'empty' }
  }

  const ceiling = model.totals.find((r) => r.kind === 'ceiling')
  if (!ceiling) return { ...base, state: 'empty' }

  // Deliberately the annual pair, never the to-date pair — see THE PERIOD
  // RULE above.
  const used = ceiling.usedAnnual || 0
  const remaining = ceiling.bakiAnnual

  // `bakiAnnual` is null only when every contributing district row had null
  // there, which for a ceiling row means there were no district rows at all.
  // Treated as empty rather than as zero remaining.
  if (remaining == null) return { ...base, state: 'empty' }

  const allocated = used + remaining

  // A scope whose contract lengths are all zero (INFS-1282 backfill not yet
  // done for a district) allocates nothing, so every percentage would divide
  // by zero. There is no utilisation to report against a zero allowance.
  if (allocated <= 0) return { ...base, state: 'empty' }

  // An unmeasured scope still has a REAL allocation, and it is returned.
  //
  // The two figures come from different places: allocation is derived from the
  // district's contract road lengths (annualQuota in src/kjp.js), while usage
  // is summed from cases. So "no case carries a usable dimension" makes usage
  // unknowable without making the allowance unknowable — R06 in current
  // production data has 149 cases, all of them unmeasured, yet its contract
  // allowance is perfectly well defined.
  //
  // Suppressing the whole tile therefore threw away a figure we knew. Only
  // `used` / `remaining` / the percentages are withheld, because only those
  // depend on measurements that were never recorded.
  if (ceiling.unmeasuredAnnual) return { ...base, state: 'unmeasured', allocated }

  return {
    ...base,
    // Over-ceiling is called out rather than shown as a negative remaining
    // that a reader has to notice the minus sign on. This is the one state
    // that warrants a status colour.
    state: remaining < 0 ? 'over' : 'ok',
    allocated,
    used,
    remaining,
    usedPct: pct(used, allocated),
    remainingPct: pct(remaining, allocated),
  }
}

// The whole tile row, in workbook scope order.
//
// Takes the same `models` / `scopeErrors` maps KjpSummary already holds, so
// the component passes its state straight through rather than reshaping it.
export function utilisationRow({ scopes, models, scopeErrors }) {
  return (scopes || []).map((scope) =>
    scopeUtilisation({
      scope,
      model: (models || {})[scope],
      failed: !!(scopeErrors || {})[scope],
    })
  )
}
